import { Cog, RefreshCw, MessageSquare } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { Card } from '../common/Card';
import { EmptyState } from '../common/EmptyState';
import type { ActivityEvent } from '../../types/activity';

interface LiveActivityFeedProps {
  events: ActivityEvent[];
  loading: boolean;
  onRefresh: () => void;
}

const EVENT_ICONS: Record<ActivityEvent['type'], React.ReactNode> = {
  job_run: <Cog className="h-4 w-4" />,
  sync: <RefreshCw className="h-4 w-4" />,
  slack: <MessageSquare className="h-4 w-4" />,
};

const STATUS_DOT_COLORS: Record<ActivityEvent['status'], string> = {
  success: 'bg-success',
  error: 'bg-error',
  running: 'bg-accent-purple animate-pulse-dot',
  info: 'bg-info',
  warning: 'bg-warning',
};

const TYPE_LABELS: Record<ActivityEvent['type'], string> = {
  job_run: 'Job',
  sync: 'Sync',
  slack: 'Slack',
};

const TYPE_LABEL_COLORS: Record<ActivityEvent['type'], string> = {
  job_run: 'bg-accent-purple/20 text-accent-purple-light',
  sync: 'bg-info/20 text-info',
  slack: 'bg-success/20 text-success',
};

const EVENT_ICON_COLORS: Record<ActivityEvent['type'], string> = {
  job_run: 'text-accent-purple-light',
  sync: 'text-info',
  slack: 'text-success',
};

function truncateSummary(text: string, max = 80): string {
  return text.length > max ? text.slice(0, max) + '...' : text;
}

export function LiveActivityFeed({ events, loading, onRefresh }: LiveActivityFeedProps) {
  return (
    <div className="w-full lg:w-80 lg:flex-shrink-0">
      <Card className="h-full flex flex-col">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-sm font-medium text-text-primary">Live Activity</h3>
            <p className="text-[10px] text-text-muted mt-0.5">Chronological timeline — all system events</p>
          </div>
          <button
            onClick={onRefresh}
            className="text-text-muted hover:text-text-secondary transition-colors p-1 rounded"
            aria-label="Refresh activity feed"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto max-h-[calc(100vh-320px)] -mx-4 px-4 space-y-1">
          {events.length === 0 ? (
            <EmptyState
              title="No recent activity"
              description="Events will appear here as jobs run"
            />
          ) : (
            events.map((event) => (
              <div
                key={event.id}
                className="flex items-start gap-2.5 py-2 border-b border-border-subtle last:border-0"
              >
                <div className={`flex-shrink-0 mt-0.5 ${EVENT_ICON_COLORS[event.type]}`}>
                  {EVENT_ICONS[event.type]}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <p className="text-sm font-medium text-text-primary truncate">
                      {event.agent}
                    </p>
                    <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium ${TYPE_LABEL_COLORS[event.type]}`}>
                      {TYPE_LABELS[event.type]}
                    </span>
                  </div>
                  <p className="text-xs text-text-muted truncate">
                    {truncateSummary(event.summary)}
                  </p>
                </div>

                <div className="flex-shrink-0 flex flex-col items-end gap-1">
                  <span className="text-[10px] text-text-muted whitespace-nowrap">
                    {formatDistanceToNow(new Date(event.timestamp), { addSuffix: true })}
                  </span>
                  <span
                    className={`h-1.5 w-1.5 rounded-full ${STATUS_DOT_COLORS[event.status]}`}
                  />
                </div>
              </div>
            ))
          )}
        </div>
      </Card>
    </div>
  );
}

export default LiveActivityFeed;
